import { useTranslation } from '@contexts/translation/hooks/useTranslation';
import { Box, Button, Icon, Input, Typography, Badge, InputProps, ButtonProps, Link } from '@drone-js/core';
import { useWalletContext } from '@v2/contexts/wallet/hooks/useWalletContext';
import React from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { PinFormCmsWithDefault } from './PinFormCmsWithDefault';
import styled from 'styled-components';
import { useWalletNavigation } from '@contexts/walletNavigation/hooks/useWalletNavigation';

const StyledBoxWithCustomMargin = styled(Box)`
    margin-top: 25px;
    margin-bottom: 37px;
`;
import { ULPath } from '@ul/common';
import { useNavHistory } from '@contexts/navigation/hooks/useNavHistory';
import { usePushNotification, useResetNotification } from '@components/Notification/useNotification';
import { isValidPin } from './PinValidation';
import { useAnalytics } from './analytics';

type PinState = 'auth' | 'confirm';
type PinIconState = 'fill.symbol.lock-locked' | 'fill.symbol.lock-unlocked';
export type PinFormProps = {
    enablePinConfirmation?: boolean;
    showForgotPin?: boolean;
    currentPath?: ULPath;
    cms: {
        initial: {
            title: string;
            caption?: string;
        };
        confirm?: {
            title: string;
            caption?: string;
        };
        disclaimer?: string;
    };
    iconState?: PinIconState;
};

const PinForm: React.FC<PinFormProps> = props => {
    const { enablePinConfirmation = false, cms, iconState = 'fill.symbol.lock-locked' } = props;

    const {
        AnalyticsPinFormProps,
        AnalyticsPinInputProps,
        AnalyticsPinSubmitButtonProps,
        AnalyticsForgotPinProps,
    } = useAnalytics();

    const [pinState, setPinState] = React.useState<PinState>('auth');
    const [pinCode, setPinCode] = React.useState<string | null>('');
    const [confirmPinCode, setConfirmPinCode] = React.useState<string | null>('');

    const [pinError, setPinError] = React.useState<string>('');

    const [buttonState, setButtonState] = React.useState<ButtonProps['state']>('disabled');

    const translation = useTranslation(PinFormCmsWithDefault);
    const isConfirmPin = pinState === 'confirm';

    const resetNotification = useResetNotification();
    const pushNotification = usePushNotification('ResetPin');

    const { goTo: goToResetPin } = useNavHistory('walletResetPin');

    const usePinConfirmation = isConfirmPin && cms.confirm;

    const formCms = {
        title: usePinConfirmation ? cms.confirm.title : cms.initial.title,
        caption: usePinConfirmation ? undefined : cms.initial.caption,
        disclaimer: !usePinConfirmation && cms.disclaimer,
        label: translation.PinFormLabel,
    };

    const onChange = (event: React.FormEvent<HTMLInputElement>) => {
        const digitRegex = /^\d+$/;
        const { value } = event.currentTarget;
        if (!value || digitRegex.test(event.currentTarget.value)) {
            !isConfirmPin ? setPinCode(value) : setConfirmPinCode(value);
        }
        if (value.length === 6) {
            setPinError(null);
        } else {
            setPinError(translation.SixDigitsError);
        }
    };

    const formRef = React.useRef(null);

    const { onPinSubmit, walletStorage } = useWalletContext();

    const { setPinRequest, currentFlow } = useStore(
        walletStorage,
        useShallow(state => ({
            setPinRequest: state.setPin,
            currentFlow: state.currentFlow,
        }))
    );

    const isBlocked = useStore(walletStorage, state => state.isBlocked);

    const { walletNavigationStore } = useWalletNavigation();

    const { setIsForgetPinFlow, setPreviousPath } = useStore(
        walletNavigationStore,
        useShallow(state => ({
            setIsForgetPinFlow: state.setIsForgetPinFlow,
            setPreviousPath: state.setPreviousPath,
        }))
    );

    const isValidConfirmPin = () => {
        if (!enablePinConfirmation) return true;
        return pinCode === confirmPinCode;
    };

    const handleButtonClick = async () => {
        if (enablePinConfirmation && pinState === 'auth') {
            setPinState('confirm');
            setButtonState('disabled');
        } else if (isValidConfirmPin()) {
            setButtonState('loading');
            setPinRequest(pinCode);
            const isSuccess = await onPinSubmit(pinCode);
            if (isSuccess !== null && !isSuccess && formRef.current) {
                onClearInput();
                setButtonState('disabled');
            }
        }
    };

    const onClearInput = () => {
        !isConfirmPin ? setPinCode('') : setConfirmPinCode('');
    };

    const onForgotPinClick = () => {
        resetNotification();
        setIsForgetPinFlow(true);
        setPreviousPath(props.currentPath);
        goToResetPin();
    };

    React.useEffect(() => {
        switch (pinState) {
            case 'auth':
                if (pinCode.length === 6 && !isBlocked) {
                    if (currentFlow === 'resetPin' && !isValidPin(pinCode)) {
                        setPinError(translation.PinSetupValidationError);
                    } else {
                        setButtonState('enabled');
                        setPinError(null);
                        break;
                    }
                    break;
                }
            case 'confirm':
                if (confirmPinCode.length === 6 && !isBlocked) {
                    if (!isValidConfirmPin()) {
                        pushNotification({
                            color: 'error',
                            message: translation.PinDoesntMatch,
                            testId: 'pin-code-doesnt-match',
                            overrideIconId: 'fill.symbol.triangle-exclamation',
                        });
                    } else {
                        resetNotification();
                        setButtonState('enabled');
                        setPinError(null);
                    }
                    break;
                }
            default:
                setButtonState('disabled');
                break;
        }
    }, [pinCode, confirmPinCode, isBlocked]);

    React.useEffect(() => {
        if (isBlocked) setButtonState('disabled');
    }, [isBlocked]);

    return (
        <Box ref={formRef} {...AnalyticsPinFormProps}>
            <Box data-cy="wallet-pin-form" mt="16" mb="12" display="flex" justifyContent="center" alignItems="center">
                <Box
                    width=""
                    display="flex"
                    p="12"
                    borderRadius="12"
                    borderStyle="solid"
                    borderWidth="1"
                    borderColor="base.subtle"
                    backgroundColor="base.background"
                    justifyContent="center"
                >
                    <Icon id={iconState} size="24" color="product.primary" />
                </Box>
            </Box>
            <Typography textAlign="center" mb={isConfirmPin ? '32' : '8'} variant="heading-xl" fontWeight={600}>
                {formCms.title}
            </Typography>
            {formCms.caption && (
                <Typography textAlign="center" mb="16" variant="body-s" fontWeight={400}>
                    {formCms.caption}
                </Typography>
            )}
            {formCms.disclaimer && (
                <Box display="flex" justifyContent="center" my="16">
                    <Badge
                        variant="secondary.intense"
                        label={formCms.disclaimer}
                        leadingAsset={{
                            id: 'fill.symbol.circle-exclamation',
                            label: 'warning sign',
                        }}
                    />
                </Box>
            )}
            <Input
                type="password"
                inputMode="numeric"
                value={isConfirmPin ? confirmPinCode : pinCode}
                // placeholder="PIN"
                maxLength={6}
                data-cy="wallet-pin-input"
                label={formCms.label}
                showPasswordLabel="Show PIN"
                onChange={onChange}
                intent={pinError ? 'error' : 'default'}
                helperText={pinError || undefined}
                clearLabel="Clear"
                onClear={onClearInput}
                {...AnalyticsPinInputProps}
            />
            {props.showForgotPin && (
                <StyledBoxWithCustomMargin display="flex" justifyContent="center">
                    <Link data-cy="wallet-forgot-pin-link" onClick={onForgotPinClick} {...AnalyticsForgotPinProps}>
                        <Typography variant="body-s-strong" textColor="product.primary">
                            {translation.ForgotPinLabel}
                        </Typography>
                    </Link>
                </StyledBoxWithCustomMargin>
            )}
            <Button
                mt="16"
                hierarchy="primary"
                width="parent-fit"
                variant="label"
                label="Submit"
                data-cy="wallet-pin-submit-button"
                state={buttonState}
                onClick={handleButtonClick}
                {...AnalyticsPinSubmitButtonProps}
            />
        </Box>
    );
};

export { PinForm };
